import { inject, Service } from '@angular/core';
import {
  GetActiveOwnerPaymentGatewaysGql,
  GetActiveOwnerPaymentGatewaysQueryResult,
  GetOwnerCreditCardGql,
  GetOwnerCreditCardQueryResult,
  GetOwnerGql,
  GetOwnerGridGql,
  GetOwnerGridQueryResult,
  GetOwnerPlannerGql,
  GetOwnerPlannerQueryResult,
  GetOwnerQueryResult,
  IsErpZucchettiExportActiveGql,
  IsErpZucchettiExportActiveQueryResult,
  Owner,
  OwnerCreditCard,
  OwnerGrid,
  OwnerPaymentGateway,
  OwnerPlanner,
} from '@h5s/entities';
import { throwErrorIfNullFromGraphQL } from '@h5s/util-error';
import { mapArrayResponse, mapResponse } from '@h5s/util-object';
import { Apollo } from 'apollo-angular';
import { map, Observable } from 'rxjs';

@Service()
export class DataAccessOwnerService {
  private readonly apollo = inject(Apollo);

  /**
   * Ottiene le impostazioni dell'Owner di 5stelle*
   *
   * @returns {Observable<Owner>} Un observable che emette l'oggetto contenente le impostazioni di 5stelle*
   */
  public getOwner(): Observable<Owner> {
    return this.apollo
      .query<GetOwnerQueryResult>({
        query: GetOwnerGql,
      })
      .pipe(
        throwErrorIfNullFromGraphQL((data) => data, '[DataAccessOwnerService] Owner not found'),
        map((owner) => mapResponse(Owner, owner.getOwner)),
      );
  }

  /**
   * Ottiene le impostazioni del planner di 5stelle*
   *
   * @returns {Observable<OwnerPlanner>} Un observable che emette l'oggetto contenente le impostazioni del planner di 5stelle*
   */
  public getOwnerPlanner(): Observable<OwnerPlanner> {
    return this.apollo
      .query<GetOwnerPlannerQueryResult>({
        query: GetOwnerPlannerGql,
      })
      .pipe(
        throwErrorIfNullFromGraphQL((data) => data, '[DataAccessOwnerService] OwnerPlanner not found'),
        map((owner) => mapResponse(OwnerPlanner, owner.getOwnerPlanner)),
      );
  }

  /**
   * Ottiene le impostazioni dei OwnerGrid di 5stelle*
   *
   * @returns {Observable<OwnerGrid>}
   */
  public getOwnerGrid(): Observable<OwnerGrid> {
    return this.apollo
      .query<GetOwnerGridQueryResult>({
        query: GetOwnerGridGql,
      })
      .pipe(
        throwErrorIfNullFromGraphQL((data) => data, '[DataAccessOwnerService] OwnerGrid not found'),
        map((result) => mapResponse(OwnerGrid, result.getOwnerGrid)),
      );
  }

  /**
   * Ottiene la configurazione owner-level per la gestione delle carte di credito
   * (gateway di default, causali predefinite, valuta, flag di abilitazione globali)
   */
  public getOwnerCreditCard(): Observable<OwnerCreditCard> {
    return this.apollo
      .query<GetOwnerCreditCardQueryResult>({
        query: GetOwnerCreditCardGql,
      })
      .pipe(
        throwErrorIfNullFromGraphQL((data) => data),
        map((result) => mapResponse(OwnerCreditCard, result.getOwnerCreditCard)),
      );
  }

  /**
   * Ottiene la lista dei gateway di pagamento attivi per l'owner corrente
   */
  public getActiveOwnerPaymentGateways(): Observable<OwnerPaymentGateway[]> {
    return this.apollo
      .query<GetActiveOwnerPaymentGatewaysQueryResult>({
        query: GetActiveOwnerPaymentGatewaysGql,
      })
      .pipe(
        throwErrorIfNullFromGraphQL((data) => data),
        map((result) => mapArrayResponse(OwnerPaymentGateway, result.getActiveOwnerPaymentGateways)),
      );
  }

  /**
   * Verifica se l'export verso Zucchetti ERP è attivo per l'owner corrente
   */
  public isErpZucchettiExportActive(): Observable<boolean> {
    return this.apollo
      .query<IsErpZucchettiExportActiveQueryResult>({
        query: IsErpZucchettiExportActiveGql,
      })
      .pipe(
        throwErrorIfNullFromGraphQL((data) => data),
        map((result) => result.isErpZucchettiExportActive),
      );
  }
}
